/*
 * CONTRARIAN LAYER — Cinematic Spatial Surface
 *
 * Owns the intro-to-crawl presentation sequence only.
 * Does not alter: V6 engine, schema, question data, answer definitions,
 * progression state, or authoritative renderer semantics.
 *
 * Sequence:
 *   mount → hide body children → show BEGIN
 *   BEGIN click → play intro.mp4 (unmuted) + start crawl
 *   crawl ends → restore body children, scroll to top
 *   user scrolls to bottom of #p47-13-history → play philosophy-outro.mp4
 *   outro ends → handoffToQuestionnaire() → #begin-examination click
 */



export function mountCinematicSpatialSurface() {
  if (document.documentElement.dataset.clCinematicMounted === "true") return;
  document.documentElement.dataset.clCinematicMounted = "true";

  /* ── STYLES ──────────────────────────────────────────────────────── */

  const style = document.createElement("style");
  style.textContent = `
    .cl-spatial-root {
      position: fixed;
      inset: 0;
      z-index: 1;
      overflow: hidden;
      background: #050505;
      color: #f1eee7;
      pointer-events: none;
      font-family: inherit;
    }

    .cl-spatial-canvas {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      opacity: .92;
    }

    .cl-spatial-vignette {
      position: absolute;
      inset: 0;
      background: radial-gradient(
        circle at 50% 46%,
        transparent 0%,
        rgba(5,5,5,.08) 42%,
        rgba(5,5,5,.72) 100%
      );
    }

    .cl-threshold-video {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      z-index: 0;
      opacity: .78;
      filter: saturate(.72) contrast(1.05) brightness(.72);
      pointer-events: none;
    }

    .cl-threshold-scroll {
      position: absolute;
      inset: 0;
      overflow: hidden;
      display: flex;
      justify-content: center;
      pointer-events: none;
      z-index: 3;
    }

    .cl-threshold-copy {
      position: absolute;
      top: 0;
      left: 50%;
      width: min(760px, calc(100vw - 64px));
      margin: 0;
      padding: 100vh 0 100vh;
      text-align: left;
      transform: translate3d(-50%, 0, 0);
      will-change: transform;
    }

    .cl-threshold-kicker {
      margin-bottom: 72px;
      text-align: center;
      font-size: 10px;
      letter-spacing: .24em;
      text-transform: uppercase;
      opacity: .48;
    }

    .cl-threshold-section { margin-bottom: 150px; }

    .cl-threshold-index {
      margin-bottom: 18px;
      font-size: 11px;
      letter-spacing: .2em;
      opacity: .42;
    }

    .cl-threshold-section h2 {
      margin: 0;
      font-size: clamp(30px, 4vw, 58px);
      line-height: 1.02;
      letter-spacing: -.035em;
      font-weight: 400;
    }

    .cl-threshold-section p {
      max-width: 680px;
      margin: 28px 0 0;
      font-size: clamp(15px, 1.25vw, 18px);
      line-height: 1.75;
      opacity: .68;
    }

    .cl-threshold-section .cl-threshold-emphasis {
      opacity: .46;
      font-size: 13px;
      letter-spacing: .025em;
    }

    .cl-threshold-end { height: 1px; }

    .cl-threshold-brand {
      position: fixed;
      top: clamp(32px, 5vh, 64px);
      left: 8ch;
      margin: 0;
      padding: 0;
      font-size: 10px;
      line-height: 1;
      letter-spacing: .24em;
      text-transform: uppercase;
      opacity: .48;
      white-space: nowrap;
      pointer-events: none;
      z-index: 40;
    }

    .cl-threshold-begin {
      position: fixed;
      left: 50%;
      bottom: clamp(48px, 8vh, 96px);
      transform: translateX(-50%);
      z-index: 60;
      pointer-events: auto;
    }

    .cl-threshold-begin button {
      display: block;
      border: 1px solid rgba(241,238,231,.28);
      background: rgba(5,5,5,.28);
      color: inherit;
      font: inherit;
      font-size: 10px;
      letter-spacing: .24em;
      line-height: 1;
      text-transform: uppercase;
      cursor: pointer;
      padding: 10px 18px;
      min-width: 92px;
      text-align: center;
    }

    .cl-threshold-begin.is-hidden {
      visibility: hidden;
      opacity: 0;
      pointer-events: none;
      transition: opacity 800ms ease;
    }

    .cl-threshold-enter {
      position: fixed;
      left: 50%;
      bottom: clamp(28px, 6vh, 64px);
      transform: translateX(-50%);
      visibility: hidden;
      opacity: 0;
      pointer-events: none;
      z-index: 50;
      transition: opacity 1200ms ease;
    }

    .cl-threshold-enter.is-visible {
      visibility: visible;
      opacity: 1;
      pointer-events: auto;
    }

    .cl-threshold-enter button {
      display: block;
      border: 1px solid rgba(241,238,231,.28);
      background: rgba(5,5,5,.22);
      color: inherit;
      font: inherit;
      font-size: 10px;
      letter-spacing: .24em;
      line-height: 1;
      text-transform: uppercase;
      cursor: pointer;
      padding: 10px 18px;
      min-width: 92px;
      text-align: center;
    }

    .cl-crawl-layer {
      position: absolute;
      inset: 0;
      z-index: 10;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition: opacity 800ms ease;
    }

    .cl-crawl-layer.is-active {
      opacity: 1;
      visibility: visible;
    }

    .cl-crawl-layer.is-complete {
      opacity: 0;
      visibility: visible;
      transition: opacity 700ms ease;
    }

    .cl-crawl-mask {
      position: absolute;
      left: 50%;
      top: 10%;
      bottom: 10%;
      width: min(680px, calc(100vw - 80px));
      transform: translateX(-50%);
      overflow: hidden;
      -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 12%, #000 88%, transparent 100%);
      mask-image: linear-gradient(180deg, transparent 0%, #000 12%, #000 88%, transparent 100%);
    }

    .cl-crawl-inner {
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      text-align: center;
      will-change: transform;
    }

    .cl-crawl-subtitle {
      margin: 0 0 56px;
      font-size: 11px;
      letter-spacing: .22em;
      text-transform: uppercase;
      opacity: .45;
    }

    .cl-crawl-roman {
      margin: 72px 0 8px;
      font-size: 10px;
      letter-spacing: .32em;
      text-transform: uppercase;
      opacity: .38;
    }

    .cl-crawl-title {
      margin: 0 0 20px;
      font-size: clamp(20px, 2.6vw, 32px);
      letter-spacing: -.02em;
      font-weight: 400;
      line-height: 1.1;
    }

    .cl-crawl-body {
      margin: 0 0 16px;
      font-size: clamp(14px, 1.3vw, 17px);
      line-height: 1.72;
      opacity: .72;
    }

    .cl-crawl-pull {
      margin: 20px 0 0;
      font-size: clamp(14px, 1.4vw, 18px);
      line-height: 1.5;
      opacity: .48;
      font-style: italic;
    }

    .cl-crawl-exit-spacer {
      display: block;
      height: 12vh;
    }

    @media (max-width: 700px) {
      .cl-threshold-copy { width: calc(100vw - 40px); }
      .cl-threshold-section { margin-bottom: 120px; }
    }
  `;
  document.head.appendChild(style);

  /* ── ROOT ELEMENT ────────────────────────────────────────────────── */

  const root = document.createElement("div");
  root.className = "cl-spatial-root";
  root.setAttribute("aria-hidden", "true");
  root.innerHTML = `<canvas class="cl-spatial-canvas"></canvas><div class="cl-spatial-vignette"></div>`;

  /* ── SAVE AND HIDE BODY CHILDREN ─────────────────────────────────── */

  const appNodes = Array.from(document.body.children).filter(el => el !== root);
  const appNodeState = appNodes.map(el => ({ el, display: el.style.display }));
  appNodes.forEach(el => { el.style.display = "none"; });

  /* Cinematic is in control — safe to reveal the document now */
  document.documentElement.style.visibility = "";

  /* ── THRESHOLD COMPONENTS ────────────────────────────────────────── */

  const thresholdScroll = document.createElement("div");
  thresholdScroll.className = "cl-threshold-scroll";

  const thresholdVideo = document.createElement("video");
  thresholdVideo.className = "cl-threshold-video";
  thresholdVideo.src = "/media/intro.mp4";
  thresholdVideo.autoplay = true;
  thresholdVideo.muted = true;
  thresholdVideo.loop = false;
  thresholdVideo.playsInline = true;
  thresholdVideo.setAttribute("webkit-playsinline", "");
  thresholdVideo.preload = "auto";
  thresholdVideo.setAttribute("aria-hidden", "true");

  thresholdScroll.innerHTML = `
    <div class="cl-threshold-copy">
      <div class="cl-threshold-kicker">THE POSITION YOU OCCUPY</div>
      <div class="cl-threshold-section">
        <div class="cl-threshold-index">I</div>
        <h2>The Economic Regime Changed</h2>
        <p>Every era of work creates a different definition of value. Industrial work rewarded execution. Knowledge work rewarded expertise. AI-augmented work rewards judgment, adaptation, and the ability to direct intelligence.</p>
        <p class="cl-threshold-emphasis">The future of work requires a new operating system.</p>
      </div>
      <div class="cl-threshold-section">
        <div class="cl-threshold-index">II</div>
        <h2>The Old Operating System</h2>
        <p>Many professionals are still navigating an AI-driven economy using assumptions built for a previous era: credentials, titles, hierarchy, and traditional career paths. The problem is not a lack of ability. The problem is outdated interpretation.</p>
        <p class="cl-threshold-emphasis">The beliefs that created your success may not create your future.</p>
      </div>
      <div class="cl-threshold-section">
        <div class="cl-threshold-index">III</div>
        <h2>The Mental Interpreter</h2>
        <p>Every decision passes through an internal framework built from experience, expectations, fear, and previous success. This framework determines what opportunities you see and what possibilities you ignore.</p>
        <p class="cl-threshold-emphasis">Before changing your career, understand the system interpreting it.</p>
      </div>
      <div class="cl-threshold-section">
        <div class="cl-threshold-index">IV</div>
        <h2>The AI Workforce Transition</h2>
        <p>Artificial intelligence is not only changing tasks. It is changing the relationship between humans and economic value. The advantage will belong to those who can combine human judgment with machine intelligence.</p>
        <p class="cl-threshold-emphasis">Adaptation begins with understanding.</p>
      </div>
      <div class="cl-threshold-section">
        <div class="cl-threshold-index">V</div>
        <h2>The Contrarian Choice</h2>
        <p>The Contrarian Layer exists to help people examine the assumptions they carry from one economic era into the next. The goal is not simply to find another role. The goal is to understand how you create value in a changing world.</p>
        <p class="cl-threshold-emphasis">Do not optimize for the old economy. Understand the one arriving.</p>
      </div>
      <div class="cl-threshold-end"></div>
    </div>
  `;

  const thresholdCopy = thresholdScroll.querySelector(".cl-threshold-copy");
  thresholdCopy.style.visibility = "hidden";
  thresholdCopy.style.opacity = "0";
  thresholdCopy.style.transform = "translate3d(-50%, 0, 0)";

  const thresholdBrand = document.createElement("div");
  thresholdBrand.className = "cl-threshold-brand";
  thresholdBrand.textContent = "THE CONTRARIAN LAYER";

  const thresholdBegin = document.createElement("div");
  thresholdBegin.className = "cl-threshold-begin";
  thresholdBegin.innerHTML = `<button type="button" aria-label="Begin the Contrarian Layer">BEGIN</button>`;

  const thresholdEnter = document.createElement("div");
  thresholdEnter.className = "cl-threshold-enter";
  thresholdEnter.innerHTML = `<button type="button" aria-label="Enter the Contrarian Layer">ENTER</button>`;

  const thresholdButton = thresholdEnter.querySelector("button");
  const thresholdBeginButton = thresholdBegin.querySelector("button");

  /* ── CRAWL ───────────────────────────────────────────────────────── */

  const crawlLayer = document.createElement("div");
  crawlLayer.className = "cl-crawl-layer";

  const crawlMask = document.createElement("div");
  crawlMask.className = "cl-crawl-mask";

  const crawlInner = document.createElement("div");
  crawlInner.className = "cl-crawl-inner";
  crawlInner.setAttribute("aria-hidden", "true");
  crawlInner.innerHTML = `
    <p class="cl-crawl-subtitle">The framework used to understand how you interpret change.</p>

    <p class="cl-crawl-roman">I</p>
    <p class="cl-crawl-title">The Economic Regime Changed</p>
    <p class="cl-crawl-body">Every era of work creates a different definition of value. Industrial work rewarded execution. Knowledge work rewarded expertise. AI-augmented work rewards judgment, adaptation, and the ability to direct intelligence.</p>
    <p class="cl-crawl-pull">The future of work requires a new operating system.</p>

    <p class="cl-crawl-roman">II</p>
    <p class="cl-crawl-title">The Old Operating System</p>
    <p class="cl-crawl-body">Many professionals are still navigating an AI-driven economy using assumptions built for a previous era: credentials, titles, hierarchy, and traditional career paths. The problem is not a lack of ability. The problem is outdated interpretation.</p>
    <p class="cl-crawl-pull">The beliefs that created your success may not create your future.</p>

    <p class="cl-crawl-roman">III</p>
    <p class="cl-crawl-title">The Mental Interpreter</p>
    <p class="cl-crawl-body">Every decision passes through an internal framework built from experience, expectations, fear, and previous success. This framework determines what opportunities you see and what possibilities you ignore.</p>
    <p class="cl-crawl-pull">Before changing your career, understand the system interpreting it.</p>

    <p class="cl-crawl-roman">IV</p>
    <p class="cl-crawl-title">The AI Workforce Transition</p>
    <p class="cl-crawl-body">Artificial intelligence is not only changing tasks. It is changing the relationship between humans and economic value. The advantage will belong to those who can combine human judgment with machine intelligence.</p>
    <p class="cl-crawl-pull">Adaptation begins with understanding.</p>

    <p class="cl-crawl-roman">V</p>
    <p class="cl-crawl-title">The Contrarian Choice</p>
    <p class="cl-crawl-body">The Contrarian Layer exists to help people examine the assumptions they carry from one economic era into the next. The goal is not simply to find another role. The goal is to understand how you create value in a changing world.</p>
    <p class="cl-crawl-pull">Do not optimize for the old economy. Understand the one arriving.</p>
    <div class="cl-crawl-exit-spacer" aria-hidden="true"></div>
  `;

  crawlMask.appendChild(crawlInner);
  crawlLayer.appendChild(crawlMask);

  /* ── PHILOSOPHY OUTRO VIDEO ──────────────────────────────────────── */

  const philosophyVideo = document.createElement("video");
  philosophyVideo.className = "cl-philosophy-video";
  philosophyVideo.src = "/media/cinematic/philosophy-outro.mp4";
  philosophyVideo.loop = false;
  philosophyVideo.playsInline = true;
  philosophyVideo.setAttribute("webkit-playsinline", "");
  philosophyVideo.preload = "auto";
  philosophyVideo.controls = false;
  philosophyVideo.muted = true;
  philosophyVideo.style.display = "none";
  philosophyVideo.setAttribute("aria-hidden", "true");

  /* ── SEQUENCE STATE ──────────────────────────────────────────────── */

  let htmlSequenceReleased = false;
  let philosophyVideoStarted = false;

  const htmlHistory = document.querySelector("#p47-13-history");

  /* ── HTML SEQUENCE RELEASE ───────────────────────────────────────── */

  const releaseHtmlSequence = () => {
    if (htmlSequenceReleased) return;
    htmlSequenceReleased = true;

    appNodeState.forEach(({ el, display }) => { el.style.display = display || ""; });

    window.scrollTo(0, 0);

    const examination05 = document.querySelector("#p47-13-examination");
    if (examination05) examination05.style.display = "none";

    const finalBtn = document.querySelector("[data-p47-13-begin]");
    if (finalBtn) {
      const scene = finalBtn.closest(".p47-13-scene");
      if (scene) scene.style.display = "none";
    }

    const enterLayerBtn = document.querySelector("#p47-13-enter");
    if (enterLayerBtn && !enterLayerBtn.dataset.wired) {
      enterLayerBtn.dataset.wired = "1";
      enterLayerBtn.addEventListener("click", () => {
        const exp = document.querySelector("#experience");
        if (exp) exp.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }

    thresholdVideo.pause();
    thresholdVideo.style.display = "none";
    thresholdBrand.style.display = "none";
    thresholdBegin.style.display = "none";
    thresholdCopy.style.display = "none";

    root.style.opacity = "0";
    root.style.transition = "opacity 600ms ease";

    if (philosophyVideo.parentNode) {
      document.body.appendChild(philosophyVideo);
    }

    window.setTimeout(() => { root.remove(); }, 620);
  };

  /* ── PHILOSOPHY VIDEO PLAYBACK ───────────────────────────────────── */

  const startPhilosophyVideo = () => {
    if (philosophyVideoStarted) return;
    philosophyVideoStarted = true;

    philosophyVideo.style.display = "block";
    philosophyVideo.currentTime = 0;

    const p = philosophyVideo.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  };

  const onHtmlSequenceScroll = () => {
    if (!htmlSequenceReleased || philosophyVideoStarted || !htmlHistory) return;

    const rect = htmlHistory.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 24) {
      window.removeEventListener("scroll", onHtmlSequenceScroll, { passive: true });
      startPhilosophyVideo();
    }
  };

  philosophyVideo.addEventListener("ended", () => {
    philosophyVideo.pause();
    philosophyVideo.style.display = "none";
    handoffToQuestionnaire();
  });

  window.addEventListener("scroll", onHtmlSequenceScroll, { passive: true });

  /* ── BEGIN CLICK ─────────────────────────────────────────────────── */

  thresholdBeginButton.addEventListener("click", () => {
    thresholdBegin.classList.add("is-hidden");

    thresholdVideo.muted = false;
    thresholdVideo.volume = 1;

    const p = thresholdVideo.play();
    if (p && typeof p.catch === "function") p.catch(() => {});

    startCrawl();
  });

  thresholdButton.addEventListener("click", () => {
    if (!thresholdEnter.classList.contains("is-visible")) return;
    thresholdButton.disabled = true;
    handoffToQuestionnaire();
  });

  /* ── CRAWL ENGINE ────────────────────────────────────────────────── */

  let crawlStarted = false;

  const startCrawl = () => {
    if (crawlStarted) return;
    crawlStarted = true;

    crawlLayer.classList.add("is-active");

    void crawlInner.offsetHeight;

    const wordCount = (crawlInner.textContent.match(/\S+/g) || []).length;
    const durationMs = Math.max(20000, (wordCount / 3.0) * 1000);
    const maskHeight = crawlMask.getBoundingClientRect().height;
    const distance = crawlInner.scrollHeight + maskHeight;
    const startTime = performance.now();

    const tick = now => {
      const progress = Math.min(1, (now - startTime) / durationMs);
      crawlInner.style.transform = `translate3d(0, ${-(distance * progress)}px, 0)`;

      if (progress < 1) {
        requestAnimationFrame(tick);
        return;
      }

      crawlLayer.classList.remove("is-active");
      crawlLayer.classList.add("is-complete");
      window.setTimeout(releaseHtmlSequence, 700);
    };

    requestAnimationFrame(tick);
  };

  /* ── DOM ASSEMBLY ────────────────────────────────────────────────── */

  root.appendChild(crawlLayer);
  thresholdScroll.prepend(thresholdVideo);
  thresholdScroll.appendChild(philosophyVideo);
  root.appendChild(thresholdScroll);
  root.appendChild(thresholdBrand);
  root.appendChild(thresholdBegin);
  root.appendChild(thresholdEnter);

  thresholdVideo.muted = true;
  thresholdVideo.pause();
  thresholdVideo.currentTime = 0;

  thresholdCopy.style.transform = "translate3d(-50%, 0, 0)";

  document.body.prepend(root);

  /* ── CANVAS ENVIRONMENT ──────────────────────────────────────────── */

  const canvas = root.querySelector(".cl-spatial-canvas");
  const ctx = canvas.getContext("2d", { alpha: true });

  let width = 0, height = 0, phase = 0;

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    width = window.innerWidth;
    height = window.innerHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = width + "px";
    canvas.style.height = height + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function draw() {
    phase += 0.003;
    ctx.clearRect(0, 0, width, height);

    const cx = width * .5;
    const cy = height * .5;

    for (let i = 0; i < 18; i++) {
      const z = i / 18;
      const drift = Math.sin(phase + i * .7) * 18;
      const x = cx + drift;
      const y = cy + Math.cos(phase * .8 + i) * 12;
      const radius = Math.min(width, height) * (.05 + z * .48);

      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(220,216,205,${0.025 + z * .025})`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    ctx.beginPath();
    ctx.moveTo(cx - width * .4, cy);
    ctx.lineTo(cx + width * .4, cy);
    ctx.strokeStyle = "rgba(220,216,205,.06)";
    ctx.stroke();

    requestAnimationFrame(draw);
  }

  window.addEventListener("resize", resize);
  resize();
  draw();

  /* ── HANDOFF TO QUESTIONNAIRE ────────────────────────────────────── */

  function handoffToQuestionnaire() {
    root.style.opacity = "0";
    root.style.pointerEvents = "none";
    root.style.transition = "opacity 900ms ease";

    appNodeState.forEach(({ el, display }) => { el.style.display = display || ""; });
    root.remove();

    const beginExamination = document.querySelector("#begin-examination");
    if (beginExamination) beginExamination.click();
  }
}

mountCinematicSpatialSurface();
